/*global Autodesk*/
import { useAppSettings } from "@api/appSettings";
import CustomFooterRowCount from "@components/DataGrid/components/footer/CustomFooterRowCount";
import { GridToolbarCustom } from "@components/DataGrid/components/toolbar/GridToolbarCustom";
import DataGrid from "@components/DataGrid/DataGrid";
import "@components/DataGrid/models/dataGridProps";
import { GridRowVisibilitySelectionPropagation } from "@components/DataGrid/models/gridRowVisibilitySelectionModel";
import { sumWithNullsAgg, truthyAgg } from "@components/DataGrid/utils/aggregation";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellParams,
  GridFilterItem,
  GridFilterModel,
  GridGroupNode,
  GridRowModel,
  GridRowSelectionPropagation,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { gridRowBackgroundColor } from "@utils/muiHelpers";
import { forwardRef, MutableRefObject, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import useLmViewerSync from "../../hooks/useLmViewerSync";
import useScenarioModel from "../../hooks/useScenarioModel";
import { FeatureManagementType } from "@api/apiClient";
import { getColumns, getFilteredColumns, getGroupingColumnDef } from "./MuiAssignGrid.columns";
import { GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD } from "@components/DataGrid/components/colDef/GridVisibilityColDef";

export interface AssignGridProps {
  apiRef: MutableRefObject<GridApiPremium>;
  modelId: string | undefined;
}

const gridStorageKey = "assign-grid";

const defaultInitialState: GridInitialStatePremium = {
  density: "compact",
  aggregation: {
    model: {
      siblings: "sum",

      rebarVolumeTotal: "sum",
      "rebar.a1A2A3": "sum",
      "rebar.a4": "sum",
      "rebar.a5": "sum",
      "rebar.b1": "sum",
      "rebar.b2": "sum",
      "rebar.b3": "sum",
      "rebar.b4": "sum",
      "rebar.b5": "sum",
      "rebar.b6": "sum",
      "rebar.b7": "sum",
      "rebar.c1": "sum",
      "rebar.c2": "sum",
      "rebar.c3": "sum",
      "rebar.c4": "sum",
      "rebar.d": "sum",
      "rebar.biogenic": "sum",
      "rebar.totalCo2": "sum",
      "rebar.a": "sum",
      "rebar.b": "sum",
      "rebar.c": "sum",

      nonModVolumeTotal: "sum",
      "non-mod.a1A2A3": "sum",
      "non-mod.a4": "sum",
      "non-mod.a5": "sum",
      "non-mod.b1": "sum",
      "non-mod.b2": "sum",
      "non-mod.b3": "sum",
      "non-mod.b4": "sum",
      "non-mod.b5": "sum",
      "non-mod.b6": "sum",
      "non-mod.b7": "sum",
      "non-mod.c1": "sum",
      "non-mod.c2": "sum",
      "non-mod.c3": "sum",
      "non-mod.c4": "sum",
      "non-mod.d": "sum",
      "non-mod.biogenic": "sum",
      "non-mod.totalCo2": "sum",
      "non-mod.a": "sum",
      "non-mod.b": "sum",
      "non-mod.c": "sum",

      quantityTotal: "sum",
      lengthTotal: "sum",
      areaTotal: "sum",
      volumeTotal: "sum",
      thickness: "sum",
      // TODO: quantity
      "element.a1A2A3": "sum",
      "element.a4": "sum",
      "element.a5": "sum",
      "element.b1": "sum",
      "element.b2": "sum",
      "element.b3": "sum",
      "element.b4": "sum",
      "element.b5": "sum",
      "element.b6": "sum",
      "element.b7": "sum",
      "element.c1": "sum",
      "element.c2": "sum",
      "element.c3": "sum",
      "element.c4": "sum",
      "element.d": "sum",
      "element.biogenic": "sum",
      "element.totalCo2": "sum",
      "element.a": "sum",
      "element.b": "sum",
      "element.c": "sum",

      assigned: "truthy",
    },
  },
  pinnedColumns: {
    left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_TREE_DATA_GROUPING_FIELD],
  },
  rowVisibilitySelection: [],
};

const aggregationFunctions = {
  ...GRID_AGGREGATION_FUNCTIONS,
  truthy: truthyAgg,
  sum: sumWithNullsAgg,
};

const slots = {
  toolbar: GridToolbarCustom,
  footerRowCount: CustomFooterRowCount,
  headerFilterMenu: null, // TODO: make this user configurable
};

const slotProps = {
  toolbar: {
    // TODO: the multiple search options are getting confusing
    showQuickFilter: false,
    showSearchFilter: true,
    searchFilterProps: {
      collapsible: true,
    },
  },
};

const getRowId = (row: any) => {
  try {
    return row.key;
  } catch (error) {
    console.error("getRowId error: ", error);
  }
};

const getTreeDataPath = (row: any) => {
  return row.path;
};

// TODO: this is causing a bug in the grid,
// when we update the Category rows with the Element rows
// there seems to be an async method running that then
// tries to access rows based on rowId and the rows are empty
// so it throws a row not found error
const getRowClassName = (params: any) => {
  if (params.row.type === "category") {
    return "mui-treetable-category";
  }
  if (params.row.type === "group") {
    // Family nodes are group type nodes with keys containing "/" (categoryId/familyIndex pattern)
    if (params.row.key && params.row.key.includes("/")) {
      return "mui-treetable-family";
    }
    return "mui-treetable-group";
  }
  if (params.row.type === "element" && params.row.leaf) {
    return "mui-treetable-element";
  }
  // TODO: this looks SUS. Why is type of element being assigned a group class?
  if (params.row.type === "element" && !params.row.leaf) {
    return "mui-treetable-group";
  }
  if (params.row.type === "component") {
    return "mui-treetable-component";
  }
  return "";
};

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: true,
  descendants: true,
};

const rowVisibilitySelectionPropagation: GridRowVisibilitySelectionPropagation = {
  parents: true,
  descendants: true,
};

const MuiAssignGridRef = forwardRef<any, PropsWithChildren<AssignGridProps>>((props, ref) => {
  console.log("MuiAssignGridRef: ", props);
  const { apiRef, modelId: inputModelId } = props;
  const theme = useTheme();
  const appStore = useAppStore();
  const [key, setKey] = useState(0); // increment key to force re-initialize of the grid
  const [modelId, setModelId] = useState<string | undefined>();
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [gridHeight, setGridHeight] = useState<string | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const filterModelRef = useRef<GridFilterModel>({ items: [] });
  const containerRef = useRef<any>(null);
  const appSettings = useAppSettings();
  const groupingColumnDef = useMemo(() => getGroupingColumnDef(i18n.t("assign-grid.columns.model")), [apiRef]);
  const { loading, co2MaterialTypeSpecKind } = useScenarioModel({ apiRef, modelId });
  useLmViewerSync({ apiRef, lmViewer: appStore.lmViewer1 });
  filterModelRef.current = filterModel;

  const scopeMap = useMemo(() => {
    return appSettings.data?.modelScopeSetting?.modelScopeSettingsItems?.reduce((acc: any, item: any) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [appSettings.data]);


  const columns = useMemo(() => getColumns(apiRef, scopeMap, appStore, theme, co2MaterialTypeSpecKind), [apiRef, scopeMap, appStore, theme, co2MaterialTypeSpecKind]);
  const { filteredColumns, columnVisibilityModel: initialVisibilityModel } = useMemo(() => getFilteredColumns(columns, appStore,appSettings.data?.profileLevelRebarSettings), [columns, appStore, appStore.applicationSettings?.features]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(initialVisibilityModel);

  const initialState = useMemo(
    () => ({
      ...defaultInitialState,
      columns: {
        columnVisibilityModel,
      },
      headerFilters: true, // this needs to be sync'd with DataGridContext.headerFilters
    }),
    [columnVisibilityModel],
  );

  useEffect(() => {
    if (inputModelId !== modelId) {
      setKey(key + 1);
      setModelId(inputModelId);
    }
  }, [inputModelId]);

  const updateGridHeight = (containerHeight: number) => {
    const padding = 8;
    const newGridHeight = `${containerHeight - padding}px`;
    console.log(`newGridHeight: ${newGridHeight}`);
    setGridHeight(newGridHeight);
  };

  useResizeObserver({
    ref: containerRef,
    onResize: ({ height }: { height: number | undefined }) => {
      if (height && height !== containerHeight) {
        setContainerHeight(height);
        updateGridHeight(height);
      }
    },
  });

  const sx = useMemo(() => {
    return {
      height: gridHeight,
      maxHeight: gridHeight,
      // Alter the row background color for elements and components
      "& .MuiDataGrid-row.mui-treetable-element:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
      "& .MuiDataGrid-row.mui-treetable-component:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
      "& .MuiDataGrid-row.mui-treetable-family:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
    };
  }, [gridHeight, appStore.colorMode]);

  const getAggregatePosition = useCallback((groupNode: GridGroupNode) => {
    return groupNode.depth === -1 ? "footer" : "inline";
  }, []);

  const handleRowSelectionChange = useCallback(
    (selection: any) => {
      console.log("Selection Changed:", selection);

      // MuiAssignTab is expecting an object with keys & value
      // to enable/disable the assign buttons
      // however, we no longer need to maintain a store of all the selected items
      // since we can just query the MUI DataGrid
      // so for now we'll just create an object with one key
      // which will trigger the button to be enabled/disabled
      var selectionCount = selection.length === 0 ? {} : { count: selection.length };
      appStore.setSelectedNodesKeys(selectionCount);

      const selectedRows = apiRef.current.getSelectedRows();
      console.log("Selected Rows: ", selectedRows);

      const cadIds: string[] = selectedRows.values().reduce((acc: any, row: any) => {
        if (row.data.cadId) {
          acc.push(row.data.cadId);
        }
        return acc;
      }, []);
      console.log("cadIds: ", cadIds);

      const urns = appStore.selectionManager.getSelectedModelUrns(appStore.scenario?.scenarioId!);
      appStore.lmViewer1?.selectByCadIds(cadIds, urns[0]);
    },
    [apiRef, appStore],
  );

  const handleCellSelectionModelChange = useCallback((selection: any) => {
    console.log("Cell Selection Changed:", selection);

    const ignoreCell = Object.values(selection).find((cells: any) => {
      return Object.entries(cells).find(([field, _value]: [string, any]) => field === GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD);
    });
    if (ignoreCell) {
      return;
    }

    apiRef.current.setRowVisibilityModel([]);
    Object.entries(selection).forEach(([rowId, cells]: [string, any]) => {
      Object.entries(cells).forEach(([field, _value]: [string, any]) => {
        if (field === GRID_TREE_DATA_GROUPING_FIELD || field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD) {
          const isRowVisibilitySelected = apiRef.current.isRowVisibilitySelected(rowId);
          apiRef.current.setRowVisibility(rowId, !isRowVisibilitySelected);
        }
      });
    });
  }, []);

  const handleRowVisibilitySelectionChange = useCallback((selection: any) => {
    console.log("Visibility Selection Changed:", selection);

    const visibleRows = apiRef.current.getVisibleRows();
    console.log("Visible Rows: ", visibleRows);

    const cadIds: string[] = visibleRows.values().reduce((acc: any, row: any) => {
      if (row.data.cadId) {
        acc.push(row.data.cadId);
      }
      return acc;
    }, []);
    console.log("cadIds: ", cadIds);

    const urns = appStore.selectionManager.getSelectedModelUrns(appStore.scenario?.scenarioId!);
    appStore.lmViewer1?.isolateByCadIds(cadIds, urns[0]);
  }, []);

  const handleFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      console.log("Filter Model Changed:", newFilterModel);

      if (!newFilterModel.items.find((item: GridFilterItem) => item.field === "cadId")) {
        if (filterModelRef.current.items.find((item: GridFilterItem) => item.field === "cadId")) {
          appStore.lmViewer1?.turnOnAllVisibility();
          // TODO: we need to turn on visibility for all rows
        }
      }

      setFilterModel(newFilterModel);
    },
    [appStore.lmViewer1],
  );

  const isCellEditable = useCallback((params: GridCellParams) => {
    if (params.colDef.editable && params.row.leaf) {
      return true;
    }
    return false;
  }, []);

  const processRowUpdate = useCallback(async (updatedRow: GridRowModel, _originalRow: GridRowModel) => {
    console.log("processRowUpdate: ", updatedRow);
    // TODO: Rebar and Non-Modelled edit validations
    return updatedRow;
  }, []);

  const processRowUpdateError = useCallback(async (error: any) => {
    console.log("processRowUpdateError: ", error);
    // TODO: Rebar and Non-Modelled edit validation errors
  }, []);

  return (
    <Box ref={containerRef} sx={{ display: "flex", flexGrow: 1, flexDirection: "column", width: "100%" }}>
      {gridHeight && (
        <DataGrid
          key={key}
          ref={ref}
          apiRef={apiRef}
          treeData
          autosizeColumns
          autosizeTreeDataGroupingColumns
          checkboxSelection
          disableRowSelectionOnClick
          getTreeDataPath={getTreeDataPath}
          columns={filteredColumns}
          // NOTE: these prevents column dimensions from being restored https://github.com/mui/mui-x/issues/17681#issuecomment-3012528602
          // and with the useSaveGridState hook, the column dimensions are restored from localStorage
          //columnVisibilityModel={columnVisibilityModel}
          //onColumnVisibilityModelChange={handleColumnVisibilityChange}
          rowSelectionPropagation={rowSelectionPropagation}
          initialState={initialState}
          groupingColDef={groupingColumnDef}
          getRowId={getRowId}
          slots={slots}
          slotProps={slotProps}
          aggregationFunctions={aggregationFunctions}
          getAggregationPosition={getAggregatePosition}
          getRowClassName={getRowClassName}
          onRowSelectionModelChange={handleRowSelectionChange}
          onRowVisibilitySelectionModelChange={handleRowVisibilitySelectionChange}
          sx={sx}
          loading={loading}
          throttleRowsMs={0}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={processRowUpdateError}
          rowVisibilitySelection
          rowVisibilitySelectionPropagation={rowVisibilitySelectionPropagation}
          cellSelection
          onCellSelectionModelChange={handleCellSelectionModelChange}
          filterModel={filterModelRef.current}
          onFilterModelChange={handleFilterModelChange}
          rowExpander
          isCellEditable={isCellEditable}
          storageKey={gridStorageKey}
        />
      )}
    </Box>
  );
});

const MuiAssignGrid = MuiAssignGridRef;
export default MuiAssignGrid;
